import React, { useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { UploadSimple, X, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * ImageUpload — accepts file or URL. Auto-compresses to max 1200px JPEG/PNG.
 * Calls onChange with the URL (either http URL or relative /api/files/...).
 * Renders preview with attached auth token query param.
 */
async function compressImage(file, maxWidth = 1200, quality = 0.85) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) return resolve(file);
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      const ratio = img.width > maxWidth ? maxWidth / img.width : 1;
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Compression failed"));
        // wrap as File for FormData
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
      }, "image/jpeg", quality);
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function imageSrc(value) {
  if (!value) return null;
  if (typeof value !== "string") return null;
  if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("blob:") || value.startsWith("data:")) return value;
  if (value.startsWith("/api/files/")) {
    const token = localStorage.getItem("aurum_token");
    const base = API_BASE.replace(/\/api$/, "");
    return `${base}${value}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
  }
  return value;
}

export default function ImageUpload({ value, onChange, testid, label, accept = "image/*", className = "" }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    try {
      setBusy(true);
      const compressed = await compressImage(file);
      const form = new FormData();
      form.append("file", compressed);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onPick = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); };

  return (
    <div className={className}>
      {label && <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5">{label}</div>}
      {value ? (
        <div className="relative rounded-lg border border-border bg-card overflow-hidden group" data-testid={testid}>
          <img src={imageSrc(value)} alt="" className="w-full h-32 object-cover" />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/70 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black"
            title="Remove"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid={testid}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          disabled={busy}
          className="w-full h-32 rounded-lg border-2 border-dashed border-border hover:border-primary/50 bg-card/40 grid place-items-center text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
        >
          <div className="text-center text-xs">
            {busy ? <span className="shimmer h-4 w-24 inline-block rounded" /> : (
              <>
                <UploadSimple size={20} className="mx-auto mb-1.5" weight="duotone" />
                <div>Click or drop image</div>
                <div className="text-[10px] uppercase tracking-wider mt-0.5">Auto-compressed</div>
              </>
            )}
          </div>
        </button>
      )}
      <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={onPick} />
    </div>
  );
}
